import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";
import { DASHBOARD_FONT, DASHBOARD_NAVY, DASHBOARD_NAVY_HOVER } from "../manage/dashboardUi";
import type { ReadinessArea, SetupReadinessModel } from "./setupReadinessAreas";

const READINESS_GOLD = "#9A6205";
const READINESS_GOLD_TRACK = "#E8D9B8";

type Props = {
  readiness: SetupReadinessModel;
  onNavigate: (targetId: string) => void;
};

const pubFont = {
  fontFamily: DASHBOARD_FONT,
} as const;

function CheckList({
  area,
  onNavigate,
}: {
  area: ReadinessArea;
  onNavigate: (targetId: string) => void;
}) {
  return (
    <Stack divider={<Divider flexItem />} sx={{ pt: 1 }}>
      {area.checks.map((check) => (
        <Stack
          key={check.id}
          direction="row"
          spacing={1}
          sx={{ py: 0.875, alignItems: "center" }}
        >
          {check.complete ? (
            <CheckCircle2 size={16} color="#2e7d32" aria-label="Complete" />
          ) : (
            <AlertTriangle size={16} color="#b45309" aria-label="Needs attention" />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{ ...pubFont, fontWeight: 700, fontSize: "0.875rem", lineHeight: 1.35 }}
            >
              {check.label}
            </Typography>
            <Typography
              sx={{
                ...pubFont,
                fontSize: "0.75rem",
                color: "text.secondary",
                lineHeight: 1.4,
              }}
            >
              {check.complete ? "Complete" : check.missingMessage}
            </Typography>
          </Box>
          {!check.complete && (
            <Button
              type="button"
              size="small"
              onClick={() => onNavigate(check.targetId)}
              aria-label={`Edit ${check.label}`}
              sx={{
                ...pubFont,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                minHeight: 30,
                color: DASHBOARD_NAVY,
              }}
            >
              Edit
            </Button>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

function ReadinessAreaCard({
  area,
  expanded,
  onToggle,
  onNavigate,
}: {
  area: ReadinessArea;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: (targetId: string) => void;
}) {
  const detailsId = `readiness-${area.id}-details`;
  const titleId = `readiness-${area.id}-title`;
  const firstIncomplete = area.checks.find((c) => !c.complete);

  if (area.complete) {
    return (
      <Paper
        component="article"
        aria-labelledby={titleId}
        variant="outlined"
        sx={{
          p: 1.25,
          borderRadius: 2,
          borderColor: "#c8d6cc",
          bgcolor: "#f7faf8",
          ...pubFont,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <CheckCircle2 size={18} color="#2e7d32" aria-hidden />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ alignItems: "center", flexWrap: "wrap" }}
            >
              <Typography
                id={titleId}
                component="h3"
                sx={{
                  ...pubFont,
                  fontWeight: 800,
                  fontSize: "0.9375rem",
                  lineHeight: 1.3,
                }}
              >
                {area.label}
              </Typography>
              <Typography
                sx={{
                  ...pubFont,
                  fontSize: "0.75rem",
                  color: "success.dark",
                  fontWeight: 800,
                }}
              >
                Complete
              </Typography>
            </Stack>
            {area.completeSummary !== "Complete" && (
              <Typography
                sx={{
                  ...pubFont,
                  display: "block",
                  fontSize: "0.75rem",
                  color: "text.secondary",
                }}
              >
                {area.completeSummary}
              </Typography>
            )}
          </Box>
          <Button
            type="button"
            size="small"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={detailsId}
            endIcon={
              <ChevronDown
                size={14}
                style={{ transform: expanded ? "rotate(180deg)" : "none" }}
              />
            }
            sx={{
              ...pubFont,
              flexShrink: 0,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8125rem",
              minHeight: 30,
              color: DASHBOARD_NAVY,
            }}
          >
            {expanded ? "Hide details" : "View details"}
          </Button>
        </Stack>
        <Collapse in={expanded} id={detailsId}>
          <CheckList area={area} onNavigate={onNavigate} />
        </Collapse>
      </Paper>
    );
  }

  return (
    <Paper
      component="article"
      aria-labelledby={titleId}
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: "#d7a72a",
        borderLeftWidth: 5,
        bgcolor: "#fffaf0",
        ...pubFont,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
        <AlertTriangle
          size={18}
          color="#b45309"
          style={{ marginTop: 2 }}
          aria-hidden
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={0.5}
            sx={{
              justifyContent: "space-between",
              alignItems: { sm: "center" },
            }}
          >
            <Typography
              id={titleId}
              component="h3"
              sx={{
                ...pubFont,
                fontWeight: 800,
                fontSize: "0.9375rem",
                lineHeight: 1.3,
              }}
            >
              {area.label}
            </Typography>
            <Chip
              size="small"
              label={`${area.incompleteCount} ${
                area.incompleteCount === 1 ? "item" : "items"
              } needs attention`}
              sx={{
                ...pubFont,
                alignSelf: { xs: "flex-start", sm: "center" },
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 24,
                bgcolor: READINESS_GOLD,
                color: "#ffffff",
                "& .MuiChip-label": { color: "#ffffff" },
              }}
            />
          </Stack>
          {firstIncomplete && (
            <Typography
              sx={{
                ...pubFont,
                display: "block",
                mt: 0.5,
                fontSize: "0.875rem",
                color: "text.primary",
                lineHeight: 1.45,
              }}
            >
              {firstIncomplete.missingMessage}
            </Typography>
          )}
        </Box>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1.25, justifyContent: "flex-end", flexWrap: "wrap" }}
      >
        {firstIncomplete && (
          <Button
            type="button"
            size="small"
            variant="contained"
            endIcon={<ArrowRight size={14} />}
            onClick={() => onNavigate(firstIncomplete.targetId)}
            sx={{
              ...pubFont,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8125rem",
              minHeight: 30,
              boxShadow: "none",
              bgcolor: DASHBOARD_NAVY,
              "&:hover": { bgcolor: DASHBOARD_NAVY_HOVER, boxShadow: "none" },
            }}
          >
            Complete this area
          </Button>
        )}
        <Button
          type="button"
          size="small"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={detailsId}
          endIcon={
            <ChevronDown
              size={14}
              style={{ transform: expanded ? "rotate(180deg)" : "none" }}
            />
          }
          sx={{
            ...pubFont,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8125rem",
            minHeight: 30,
            color: DASHBOARD_NAVY,
          }}
        >
          {expanded ? "Hide details" : "View details"}
        </Button>
      </Stack>
      <Collapse in={expanded} id={detailsId}>
        <CheckList area={area} onNavigate={onNavigate} />
      </Collapse>
    </Paper>
  );
}

export default function PublicationReadinessDashboard({
  readiness,
  onNavigate,
}: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const needsAttention = readiness.areas.filter((a) => !a.complete);
  const complete = readiness.areas.filter((a) => a.complete);
  const nextCheck = needsAttention[0]?.checks.find((c) => !c.complete);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box
      sx={{
        ...pubFont,
        "&, & *": pubFont,
        "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiChip-label":
          pubFont,
      }}
    >
      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 1.75 }, borderRadius: 2.5 }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          sx={{ justifyContent: "space-between", alignItems: { md: "center" } }}
        >
          <Box
            role="status"
            aria-live="polite"
            aria-atomic="true"
            sx={{ minWidth: 0 }}
          >
            <Typography
              sx={{
                ...pubFont,
                fontWeight: 800,
                fontSize: "1.125rem",
                lineHeight: 1.3,
              }}
            >
              Publication Readiness
            </Typography>
            <Typography
              sx={{
                ...pubFont,
                mt: 0.5,
                fontWeight: 700,
                fontSize: "0.9375rem",
                lineHeight: 1.35,
              }}
            >
              {readiness.completedAreaCount} of {readiness.areas.length} readiness
              areas complete
            </Typography>
            <Typography
              sx={{
                ...pubFont,
                mt: 0.25,
                fontSize: "0.875rem",
                color: "text.secondary",
                lineHeight: 1.45,
              }}
            >
              {readiness.readyForReview
                ? "All requirements are complete. This retreat can be submitted for publication review."
                : `${readiness.incompleteAreaCount} ${
                    readiness.incompleteAreaCount === 1
                      ? "readiness area needs"
                      : "readiness areas need"
                  } attention before registration can open.`}
            </Typography>
            <Typography
              sx={{
                ...pubFont,
                display: "block",
                mt: 1,
                fontSize: "0.75rem",
                color: "text.secondary",
                lineHeight: 1.4,
              }}
            >
              Only requirements needed to open registration are shown here. Other
              retreat preparation can continue separately.
            </Typography>
          </Box>
          {nextCheck && (
            <Button
              type="button"
              size="small"
              variant="contained"
              endIcon={<ArrowRight size={14} />}
              onClick={() => onNavigate(nextCheck.targetId)}
              sx={{
                ...pubFont,
                alignSelf: { xs: "stretch", md: "center" },
                whiteSpace: "nowrap",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                minHeight: 32,
                boxShadow: "none",
                bgcolor: DASHBOARD_NAVY,
                "&:hover": { bgcolor: DASHBOARD_NAVY_HOVER, boxShadow: "none" },
              }}
            >
              Complete Next Item
            </Button>
          )}
        </Stack>
        <Box sx={{ mt: 1.25 }}>
          <LinearProgress
            variant="determinate"
            value={readiness.areaPercentage}
            aria-label={`${readiness.areaPercentage}% of readiness areas complete`}
            sx={{
              height: 6,
              borderRadius: 10,
              bgcolor: READINESS_GOLD_TRACK,
              "& .MuiLinearProgress-bar": {
                borderRadius: 10,
                bgcolor: readiness.readyForReview ? "#2e7d32" : READINESS_GOLD,
              },
            }}
          />
          <Typography
            sx={{
              ...pubFont,
              fontSize: "0.75rem",
              color: "text.secondary",
              mt: 0.35,
            }}
          >
            {readiness.areaPercentage}% of readiness areas complete
          </Typography>
        </Box>
      </Paper>

      {needsAttention.length > 0 && (
        <Box component="section" aria-labelledby="needs-attention-heading" sx={{ mt: 2 }}>
          <Typography
            id="needs-attention-heading"
            sx={{
              ...pubFont,
              mb: 1,
              fontWeight: 800,
              fontSize: "1.0625rem",
              lineHeight: 1.3,
            }}
          >
            Needs Attention
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
              gap: 1.25,
            }}
          >
            {needsAttention.map((area) => (
              <ReadinessAreaCard
                key={area.id}
                area={area}
                expanded={expanded.has(area.id)}
                onToggle={() => toggle(area.id)}
                onNavigate={onNavigate}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box component="section" aria-labelledby="complete-heading" sx={{ mt: 2 }}>
        <Typography
          id="complete-heading"
          sx={{
            ...pubFont,
            mb: 1,
            fontWeight: 800,
            fontSize: "1.0625rem",
            lineHeight: 1.3,
          }}
        >
          Complete
        </Typography>
        {complete.length > 0 ? (
          <Stack spacing={0.75}>
            {complete.map((area) => (
              <ReadinessAreaCard
                key={area.id}
                area={area}
                expanded={expanded.has(area.id)}
                onToggle={() => toggle(area.id)}
                onNavigate={onNavigate}
              />
            ))}
          </Stack>
        ) : (
          <Typography
            sx={{
              ...pubFont,
              fontSize: "0.875rem",
              color: "text.secondary",
            }}
          >
            Completed readiness areas will appear here.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
